import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { Field, reduxForm, change } from 'redux-form';

import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';
import LoadingWrapper from 'components/Common/LoadingWrapper';
import RadioTableGroup from 'components/Common/Inputs/RadioTableGroup';
import ModalWrapper from 'components/Modals/ModalWrapper';
import CICReviewerComments from 'components/CICReviewerComments';
import WorkflowStages from 'common/Workflows/WorkflowStages';

import {
  getStorageSetting,
  getRtk,
  saveCicReviewState
} from 'actions';

class ReasonToKnowModal extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    permissions: PropTypes.arrayOf(PropTypes.string),
    isShown: PropTypes.bool.isRequired,
    isRtkLoading: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    id: PropTypes.number,
    rtk: PropTypes.shape({
      isCic: PropTypes.bool,
      analysis: PropTypes.shape({
        changes: PropTypes.arrayOf(PropTypes.shape())
      }),
      workflow: PropTypes.shape(),
      referenceCode: PropTypes.string,
      customerDbId: PropTypes.number,
      review: PropTypes.shape()
    })
  };

  static defaultProps = {
    permissions: null,
    isRtkLoading: false,
    id: null,
    rtk: null
  }

  constructor(props) {
    super();

    this.state = {
      isCic: props.rtk != null ? props.rtk.isCic : null,
      currentChanges: props.rtk != null && props.rtk.analysis != null ? props.rtk.analysis.changes : null,
      submissionError: null
    };
  }

  componentDidMount() {
    const { dispatch, id } = this.props;

    dispatch(getRtk(id));
  }

  static getDerivedStateFromProps(props, state) {
    if (state.currentChanges == null
      && props.rtk != null
      && props.rtk.analysis != null
      && props.rtk.analysis.changes != null) {
      return {
        isCic: props.rtk.isCic,
        currentChanges: props.rtk.analysis.changes
      };
    }
    return {};
  }

  componentDidUpdate(prevProps, prevState) {
    const { dispatch, rtk } = this.props;

    if (prevProps.rtk == null && rtk != null) {
      dispatch(change('reasonToKnowForm', 'isCic', rtk.isCic));

      if (rtk.analysis != null && rtk.analysis.changes != null) {
        rtk.analysis.changes.forEach((chg, index) => {
          dispatch(change('reasonToKnowForm', `cicReason[${ index }]`, chg.cicReason));
          dispatch(change('reasonToKnowForm', `cicSubCategory[${ index }]`, chg.cicSubCategory));
        });
      }
    }
  }

  onCicChange(e, newVal, prevVal) {
    this.setState({ isCic: newVal ? (newVal === 'true') : null });
  }

  onCicReasonChange = (row, e) => {
    const { dispatch } = this.props;
    const { currentChanges } = this.state;

    if (currentChanges[row] == null) {
      return;
    }

    currentChanges[row].cicReason = parseInt(e.target.value, 10);
    currentChanges[row].cicSubCategory = '';

    this.setState({ currentChanges }, () => {
      dispatch(change('reasonToKnowForm', `cicSubCategory[${ row }]`, ''));
    });
  };

  onCicSubCategoryChange = (row, e) => {
    const { currentChanges } = this.state;

    if (currentChanges[row] == null) {
      return;
    }

    currentChanges[row].cicSubCategory = parseInt(e.target.value, 10);

    this.setState({ currentChanges });
  };

  onFormSubmit(id, workflowAction) {
    const { dispatch } = this.props;
    const { currentChanges, isCic } = this.state;
    let submissionError = null;

    if (workflowAction != null) {
      if (workflowAction.actionId === '1') {
        // Submit button pressed

        if (isCic === null) {
          submissionError = { code: 500, message: 'Please answer \'Yes\' or \'No\' to the question above before submitting this decision' };
        } else if (!workflowAction.draftComments) {
          submissionError = { code: 500, message: 'Please enter reviewer comments before submitting this decision' };
        }
      }

      this.setState({ submissionError }, () => {
        if (submissionError != null) {
          if (this.submissionErrors) {
            this.submissionErrors.scrollIntoView({ behavior: 'smooth' });
          }
          return;
        }

        const reviewState = Object.assign({}, workflowAction);

        reviewState.isCic = isCic;
        reviewState.changes = currentChanges;

        dispatch(saveCicReviewState(id, reviewState));
      });
    }
  }

  onClearSubmissionError() {
    this.setState({ submissionError: null });
  }

  getSubCategoryOptions(row) {
    const { rtk } = this.props;

    if (rtk == null || rtk.analysis == null || rtk.analysis.changes == null || rtk.analysis.changes[row] == null) {
      return [];
    }

    const { cicReason } = rtk.analysis.changes[row];

    switch (cicReason) {
      case 1:
        return [];
      case 2:
        return [
          { value: 1, text: 'CPs who were not previously US Citizens for tax purposes become US persons for tax purposes' },
          { value: 2, text: 'CPs who were not previously a tax resident in a CRS Participating tax jurisdiction who now are' },
          { value: 3, text: 'CPs tax resident in an additional CRS Participating tax jurisdiction' },
          { value: 4, text: 'Change to the type of Controlling Persons' },
        ];
      case 3:
        return [
          { value: 1, text: 'Switch from Active Income to Passive Income' },
          { value: 2, text: 'Switch from Passive Income to Active Income' },
          { value: 3, text: 'Change in tax jurisdiction' },
          { value: 4, text: 'Removal of GIIN from the IRS GIIN list' },
          { value: 5, text: 'Change to the Controlling Persons' },
          { value: 6, text: 'Bankruptcy/Liquidation' },
          { value: 7, text: 'Tax Jurisdiction changes from IGAM2 to IGAM1' },
          { value: 8, text: 'W8/W9/CRS form/other self-certification has a different change status' },
          { value: 9, text: 'Corporate Action e.g. listing on a stock market' },
          { value: 10, text: 'Low Value to High Value i.e. >$1M' },
          { value: 11, text: 'Pre-existing Entity Account that now has a balance or value > $250K' },
        ];
      case 4:
        return [];
      case 5:
        return [
          { value: 1, text: 'Switch from Active Income to Passive Income' },
          { value: 2, text: 'Switch from Passive Income to Active Income' },
          { value: 3, text: 'Change in tax jurisdiction' },
          { value: 4, text: 'Removal of GIIN from the IRS GIIN list' },
          { value: 5, text: 'Change to the Controlling Persons' },
          { value: 6, text: 'Bankruptcy/Liquidation' },
          { value: 7, text: 'Tax Jurisdiction changes from IGAM2 to IGAM1' },
          { value: 8, text: 'W8/W9/CRS form/other self-certification has a different change status' },
          { value: 9, text: 'Corporate Action e.g. listing on a stock market' },
          { value: 10, text: 'Low Value to High Value i.e. >$1M' },
          { value: 11, text: 'The current FATCA Status has a 24-month expiring date' },
        ];
      case 6:
        return [
          { value: 1, text: 'Joint account that was not previously a joint account' },
          { value: 2, text: 'Marriage/Divorce' },
          { value: 3, text: 'Deed poll' },
          { value: 4, text: 'Change to Company Name' }
        ];
      case 7:
        return [
          { value: 1, text: 'Address in CRS participating jurisdiction where previously there was none' },
          { value: 2, text: 'Address in an additional CRS participating jurisdiction' },
          { value: 3, text: 'Tax residences of controlling persons of CRS reportable entities' }
        ];
      case 8:
        return [
          { value: 1, text: 'Non-US TIN supplied where previously one was not supplied via self-certification' },
          { value: 2, text: 'Customer has a TIN/FTIN but has not yet disclosed the TIN/FTIN' },
          { value: 3, text: 'TIN supplied for a tax jurisdiction for which no TIN was previously obtained' }
        ];
      case 9:
        return [
          { value: 1, text: 'Green Card Holder' },
          { value: 2, text: 'Address in the US' },
          { value: 3, text: 'Standing instructions to pay amounts from a foreign (meaning non U.S.) account' },
          { value: 4, text: 'A current power of attorney or signatory authority granted to a person with a U.S. address' },
          { value: 5, text: 'US telephone number' }
        ];
      default:
        return [];
    }
  }

  isValidReviewer() {
    const { permissions, rtk } = this.props;

    if (rtk == null || rtk.workflow == null) {
      return false;
    }

    // WF Stage 1 or 2 - Draft or Pending Signature
    if (rtk.workflow.stage === WorkflowStages.Draft || rtk.workflow.stage === WorkflowStages.PendingSignature) {
      return true;
    }

    const isOwner = rtk.workflow != null && rtk.workflow.assignedUser === getStorageSetting('username');

    // WF Stage 3 or 4 and Initial Reviewer
    // WF Stage 5 or 6 and Secondary Reviewer
    // WF Stage 7, 8 or 100 and Expert Reviewer
    // WF Stage 100 and Supplementary Reviewer
    const initialReviewer = (rtk.workflow.stage === WorkflowStages.PendingReview || rtk.workflow.stage === WorkflowStages.UnderInitialReview)
      && AccessControl.hasPermission(permissions, [ActivityPerms.cicwfInitialReviewer]);
    const secondaryReviewer = (rtk.workflow.stage === WorkflowStages.PendingSecondaryReview || rtk.workflow.stage === WorkflowStages.UnderSecondaryReview)
      && AccessControl.hasPermission(permissions, [ActivityPerms.cicwfSecondaryReviewer]);
    const expertReviewer = (rtk.workflow.stage === WorkflowStages.PendingExpertReview || rtk.workflow.stage === WorkflowStages.UnderExpertReview || rtk.workflow.stage === WorkflowStages.Complete)
      && AccessControl.hasPermission(permissions, [ActivityPerms.cicwfExpertReviewer]);
    const suppReviewer = rtk.workflow.stage === 100 && AccessControl.hasPermission(permissions, [ActivityPerms.cicwfSupplReviewer]);

    if (rtk.workflow.stage === WorkflowStages.Complete) {
      return (expertReviewer || suppReviewer);
    }

    return (isOwner && (initialReviewer || secondaryReviewer || expertReviewer || suppReviewer));
  }

  render() {
    const {
      isShown,
      onClose,
      id,
      isRtkLoading,
      rtk
    } = this.props;
    const { currentChanges, isCic, submissionError } = this.state;

    const validReviewer = this.isValidReviewer();

    return (
      <ModalWrapper
        isOpen={ isShown }
        title={ `RtK ${ rtk != null ? rtk.referenceCode : '' }` }
        btnCancelTitle="Close"
        onCancel={ onClose }
        showFooter={ false }
        modalSize="modal-lg rtk-modal"
      >
        { (isRtkLoading || rtk == null) && <LoadingWrapper loaded={ !isRtkLoading } /> }
        { (!isRtkLoading && rtk != null) && <div>
          <div className="form-group">
            <div className="col-12">
              <label htmlFor="cic-choice" className="form-label text-bold">{ `Is RtK ${ rtk != null ? rtk.referenceCode : '' } a change in circumstance?` }</label>
            </div>
            <div className="col-12">
              { validReviewer && <RadioTableGroup
                name="isCic"
                items={ [[{ text: 'Yes', value: 'true' }, { text: 'No', value: 'false' }]] }
                preventReset
                normalize={ value => value }
                onChange={ (e, newVal, prevVal) => this.onCicChange(e, newVal, prevVal) }
                disabled={ !validReviewer }
              /> }
              {
                // eslint-disable-next-line no-nested-ternary
                !validReviewer && <label className="form-label text-bold">{ rtk.isCic != null ? rtk.isCic ? 'Yes' : 'No' : 'N/A' }</label>
              }
            </div>
          </div>
          <div className="changes-table">
            <table className="table table-striped table-hover table-bordered">
              <thead>
                <tr>
                  <th className="col-2">Type</th>
                  <th className="col-2">Previous</th>
                  <th className="col-2">Current</th>
                  <th className="col-3">Reason for CiC</th>
                  <th className="col-3">Sub-category</th>
                </tr>
              </thead>
              <tbody>
                { /* eslint-disable react/no-array-index-key */
                  /* eslint-disable arrow-body-style */
                  currentChanges != null && currentChanges.map((chg, index) => chg != null && <tr key={ index }>
                    <td align="center">{ chg.changeTypeDescription != null ? chg.changeTypeDescription : '' }</td>
                    <td align="center">
                      { // eslint-disable-next-line
                        chg.previous != null && chg.previous.split('\n') && chg.previous.split('\n').map((item, index) => item != null && <span key={ index }>{ item }<br /></span>) 
                      }
                    </td>
                    <td align="center">
                      { // eslint-disable-next-line
                        chg.current != null && chg.current.split('\n') && chg.current.split('\n').map((item, index) => item != null && <span key={ index }>{ item }<br /></span>) 
                      }
                    </td>
                    <td align="center">
                      <Field
                        id={ `dll-cicreason-${ index }` }
                        name={ `cicReason[${ index }]` }
                        component="select"
                        value={ chg.cicReason != null ? chg.cicReason.toString() : '' }
                        className="form-select"
                        onChange={ e => this.onCicReasonChange(index, e) }
                        disabled={ !validReviewer || !isCic }
                      >
                        <option value="" className="list-header">Choose a reason</option>
                        <option value="1">Change from Low Value to High Value</option>
                        <option value="2">Controlling Persons</option>
                        { /* <option value="3">CRS Status</option> */ }
                        <option value="4">Dormant to Other Than Dormant</option>
                        <option value="5">FATCA Status</option>
                        <option value="6">Name of Account Holder(s)</option>
                        <option value="7">Tax Residency</option>
                        <option value="8">TIN/FTIN</option>
                        <option value="9">US Citizenship</option>
                      </Field>
                    </td>
                    <td align="center">
                      { chg.cicReason !== 1 && chg.cicReason !== 4 && <Field
                        id={ `ddl-subcategory-${ index }` }
                        name={ `cicSubCategory[${ index }]` }
                        component="select"
                        className="form-select"
                        value={ chg.cicSubCategory != null ? chg.cicSubCategory.toString() : '' }
                        onChange={ e => this.onCicSubCategoryChange(index, e) }
                        disabled={ !validReviewer || !isCic }
                      >
                        <option value="" className="list-header">Choose a sub-category</option>
                        { this.getSubCategoryOptions(index).map((opt, oIndex) => <option key={ oIndex } value={ opt.value }>{ opt.text }</option>) }
                      </Field> }
                    </td>
                  </tr>)
                  /* eslint-enable arrow-body-style */
                  /* eslint-enable react/no-array-index-key */
                }
              </tbody>
            </table>
          </div>
          { rtk != null && rtk.review && <CICReviewerComments
            id={ !Number.isNaN(id) ? parseInt(id, 10) : null }
            customerId={ !Number.isNaN(rtk.customerDbId) ? parseInt(rtk.customerDbId, 10) : null }
            cicReviewState={ rtk.review }
            validReviewer={ this.isValidReviewer() }
            onSubmit={ (wfId, values) => this.onFormSubmit(wfId, values) }
          /> }
          { submissionError && submissionError.code !== 0 && <div
            className="toast toast-error"
            ref={ (el) => { this.submissionErrors = el; } }
          >
            <button type="button" onClick={ () => this.onClearSubmissionError() } className="btn btn-clear float-right" />
            { submissionError.message }
          </div>}
        </div> }
      </ModalWrapper>
    );
  }
}

const ReasonToKnowForm = reduxForm({
  form: 'reasonToKnowForm',
  enableReinitialize: true
})(ReasonToKnowModal);

function mapStateToProps(state) {
  const {
    auth: {
      permissions
    },
    rtks: {
      isRtkLoading,
      rtk
    }
  } = state;

  return {
    permissions,
    isRtkLoading,
    rtk
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ReasonToKnowForm);
